import { ReactNode, useRef, useState, useEffect } from 'react';

import { Box } from '@ui';

export type LazyUnmountWrapperProps = {
  children: ReactNode;
  enabled?: boolean; // If true, unmount children when out of viewport
};

/**
 * Wrapper component that unmounts children when out of viewport to save memory.
 * Preserves the last known height to prevent layout shifts during scrolling.
 * Falls back to always rendering children if IntersectionObserver is not supported.
 */
export const LazyUnmountWrapper = ({
  children,
  enabled = false
}: LazyUnmountWrapperProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInViewport, setIsInViewport] = useState(true);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [lastKnownHeight, setLastKnownHeight] = useState<number>(80); // Default minimum height
  const [isSupported, setIsSupported] = useState(true);

  // Check if browser supports IntersectionObserver and ResizeObserver
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      (!window.IntersectionObserver || !window.ResizeObserver)
    ) {
      setIsSupported(false);
    }
  }, []);

  // Track the height using ResizeObserver (always, not just when in viewport)
  // This ensures we capture height changes from accordion collapse/expand even when out of viewport
  useEffect(() => {
    if (!enabled || !isSupported || !containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        if (height > 0) {
          setLastKnownHeight(height);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [enabled, isSupported]);

  // IntersectionObserver to detect when container is in viewport
  useEffect(() => {
    if (!enabled || !isSupported || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting;
          setIsInViewport(isVisible);

          // Once visible, mark as "has been visible" to render content initially
          if (isVisible && !hasBeenVisible) {
            setHasBeenVisible(true);
          }
        });
      },
      {
        // Use a large root margin to load content slightly before entering viewport
        // This provides a smoother experience
        rootMargin: '200px 0px',
        threshold: 0
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [enabled, isSupported, hasBeenVisible]);

  // Determine if we should render children
  // If enabled and out of viewport and has been visible before, unmount children
  // If browser doesn't support observers, always render children (fallback)
  const shouldRenderChildren =
    !enabled || !isSupported || isInViewport || !hasBeenVisible;

  // Only apply minHeight when out of viewport to preserve space
  // When in viewport, let the content size naturally
  // Don't apply minHeight if observers not supported
  const shouldPreserveHeight =
    enabled && isSupported && !isInViewport && hasBeenVisible;

  return (
    <Box
      ref={containerRef}
      style={{
        minHeight: shouldPreserveHeight ? `${lastKnownHeight}px` : undefined
      }}
      w="100%"
    >
      {shouldRenderChildren ? children : null}
    </Box>
  );
};
